import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box, Button, Dialog, Drawer, useMediaQuery } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import i18n from "../../../../utils/i18n.js";
import classes from "./howItWorksPanel.module.scss";

const STEP_CARDS = [
  {
    number: "01",
    title: i18n.t("searchflow.step1title"),
    description: i18n.t("searchflow.step1desc"),
  },
  {
    number: "02",
    title: i18n.t("searchflow.step2title"),
    description: i18n.t("searchflow.step2desc"),
  },
  {
    number: "03",
    title: i18n.t("searchflow.step3title"),
    description: i18n.t("searchflow.step3desc"),
  },
  {
    number: "04",
    title: i18n.t("searchflow.step4title"),
    description: i18n.t("searchflow.step4desc"),
  },
  {
    number: "05",
    title: i18n.t("searchflow.step5title"),
    description: i18n.t("searchflow.step5desc"),
  },
];

const getSnapScrollLeft = (card, carousel) => {
  const maxScroll = Math.max(0, carousel.scrollWidth - carousel.clientWidth);
  const centered =
    card.offsetLeft + card.offsetWidth / 2 - carousel.clientWidth / 2;
  return Math.max(0, Math.min(centered, maxScroll));
};

const getActiveIndexFromScroll = (carousel) => {
  const cards = carousel.querySelectorAll("[data-carousel-card]");
  if (!cards.length) {
    return 0;
  }

  const scrollLeft = carousel.scrollLeft;
  let closestIndex = 0;
  let closestDistance = Number.POSITIVE_INFINITY;

  cards.forEach((card, index) => {
    const snapLeft = getSnapScrollLeft(card, carousel);
    const distance = Math.abs(scrollLeft - snapLeft);
    if (distance < closestDistance) {
      closestDistance = distance;
      closestIndex = index;
    }
  });

  return closestIndex;
};

const submitButtonSx = {
  textTransform: "none",
  minHeight: 48,
  borderRadius: "8px",
  fontFamily: "Satoshi, sans-serif",
  fontSize: 16,
  fontWeight: 600,
  backgroundColor: "#1c32f3",
  color: "#fff",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "#1528c4",
    boxShadow: "none",
  },
};

const dialogPaperSx = {
  width: "calc(100% - 48px)",
  maxWidth: 520,
  margin: "32px auto",
  borderRadius: "16px",
  boxShadow: "0 8px 32px rgba(2, 6, 23, 0.12)",
  overflow: "hidden",
};

const HowItWorksPanel = ({ open, onClose, onContinue }) => {
  const isMobileLayout = useMediaQuery("(max-width:599.98px)");
  const [isDragging, setIsDragging] = useState(false);
  const carouselRef = useRef(null);
  const dragStateRef = useRef({
    active: false,
    startX: 0,
    startScrollLeft: 0,
    pointerId: null,
  });

  const snapToNearestCard = useCallback((behavior = "smooth") => {
    const carousel = carouselRef.current;
    if (!carousel) {
      return;
    }

    const cards = carousel.querySelectorAll("[data-carousel-card]");
    if (!cards.length) {
      return;
    }

    const activeIndex = getActiveIndexFromScroll(carousel);
    const targetCard = cards[activeIndex];
    const snapLeft = getSnapScrollLeft(targetCard, carousel);

    if (Math.abs(carousel.scrollLeft - snapLeft) < 2) {
      return;
    }

    carousel.scrollTo({ left: snapLeft, behavior });
  }, []);

  const scrollToCard = useCallback((index, behavior = "smooth") => {
    const carousel = carouselRef.current;
    if (!carousel) {
      return;
    }

    const cards = carousel.querySelectorAll("[data-carousel-card]");
    const targetCard = cards[index];
    if (!targetCard) {
      return;
    }

    const snapLeft = getSnapScrollLeft(targetCard, carousel);
    carousel.scrollTo({ left: snapLeft, behavior });
  }, []);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const rafId = requestAnimationFrame(() => {
      scrollToCard(0, "auto");
    });

    return () => cancelAnimationFrame(rafId);
  }, [open, scrollToCard]);

  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel || !open) {
      return undefined;
    }

    let rafId;
    const handleScrollEnd = () => {
      if (!dragStateRef.current.active) {
        snapToNearestCard("smooth");
      }
    };

    carousel.addEventListener("scrollend", handleScrollEnd);

    return () => {
      cancelAnimationFrame(rafId);
      carousel.removeEventListener("scrollend", handleScrollEnd);
    };
  }, [open, snapToNearestCard]);

  const handlePointerDown = useCallback((event) => {
    const carousel = carouselRef.current;
    if (!carousel || (event.pointerType === "mouse" && event.button !== 0)) {
      return;
    }

    dragStateRef.current = {
      active: true,
      startX: event.clientX,
      startScrollLeft: carousel.scrollLeft,
      pointerId: event.pointerId,
    };
    setIsDragging(true);
    carousel.setPointerCapture(event.pointerId);
  }, []);

  const handlePointerMove = useCallback((event) => {
    const carousel = carouselRef.current;
    const dragState = dragStateRef.current;
    if (
      !carousel ||
      !dragState.active ||
      dragState.pointerId !== event.pointerId
    ) {
      return;
    }

    const deltaX = event.clientX - dragState.startX;
    carousel.scrollLeft = dragState.startScrollLeft - deltaX;
  }, []);

  const endDrag = useCallback(
    (event) => {
      const carousel = carouselRef.current;
      const dragState = dragStateRef.current;
      if (
        !carousel ||
        !dragState.active ||
        dragState.pointerId !== event.pointerId
      ) {
        return;
      }

      dragState.active = false;
      dragState.pointerId = null;
      setIsDragging(false);

      if (carousel.hasPointerCapture(event.pointerId)) {
        carousel.releasePointerCapture(event.pointerId);
      }

      snapToNearestCard("smooth");
    },
    [snapToNearestCard],
  );

  const handleContinue = useCallback(() => {
    onClose?.();
    onContinue?.();
  }, [onClose, onContinue]);

  const panelBody = (
    <Box className={classes.content}>
      {isMobileLayout && <div className={classes.handle} aria-hidden="true" />}
      <Box className={classes.scrollBody}>
        <h2 id="how-it-works-title" className={classes.title}>
          {i18n.t("searchflow.howitworkstitle")}
        </h2>

        <p className={classes.subtitle}>
          {i18n.t("searchflow.howitworkssubtitle")}
        </p>

        <div
          ref={carouselRef}
          className={`${classes.stepCarousel} ${isDragging ? classes.stepCarouselDragging : ""}`}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onDragStart={(event) => event.preventDefault()}
          aria-label={i18n.t("searchflow.stepslabel")}
        >
          {STEP_CARDS.map((card) => (
            <article
              key={card.number}
              className={classes.stepCard}
              data-carousel-card
            >
              <span className={classes.stepNumber} aria-hidden="true">
                {card.number}
              </span>
              <h3 className={classes.stepTitle}>{card.title}</h3>
              <p className={classes.stepDescription}>{card.description}</p>
            </article>
          ))}
        </div>
      </Box>

      <Box className={classes.footer}>
        <Button
          type="button"
          variant="contained"
          fullWidth
          endIcon={<ChevronRightIcon sx={{ color: "#fff" }} />}
          sx={submitButtonSx}
          onClick={handleContinue}
        >
          {i18n.t("searchflow.requestaccountantcta")}
        </Button>
        <p className={classes.disclaimer}>
          {i18n.t("searchflow.howitworksdisclaimer")}
        </p>
      </Box>
    </Box>
  );

  if (isMobileLayout) {
    return (
      <Drawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        PaperProps={{ className: classes.drawerPaper }}
      >
        {panelBody}
      </Drawer>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="how-it-works-title"
      maxWidth={false}
      PaperProps={{ sx: dialogPaperSx }}
    >
      {panelBody}
    </Dialog>
  );
};

export default HowItWorksPanel;
